import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiService, ApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const EmailConfirmation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { token: urlToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();
  
  // Get token from either URL parameter or query parameter
  const token = urlToken || searchParams.get('token');
  const [isProcessing, setIsProcessing] = useState(true);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  
  // Use ref to persist confirmation state across React StrictMode remounts
  const confirmationAttempted = useRef(false);

  const confirmEmail = useCallback(async () => {
    // Prevent multiple calls using both state and ref
    if (hasConfirmed || confirmationAttempted.current || !token) {
      if (!token && !confirmationAttempted.current) {
        const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
        const emailConfirmedRoute = t('routes.emailConfirmed');
        navigate(`/${currentLanguage}${emailConfirmedRoute}?success=false`);
        setIsProcessing(false);
      }
      return;
    }

    // Mark as confirmed to prevent race conditions (both state and ref)
    setHasConfirmed(true);
    confirmationAttempted.current = true;

    try {
      const response = await apiService.confirmEmail(token);
      
      // If we get here, the API call was successful (200 response)
      // Now authenticate the user with the JWT token from the response
      const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
      const emailConfirmedRoute = t('routes.emailConfirmed');
      
      if (response.success && response.token && response.account) {
        // Use auth context to properly set authenticated user
        setAuthenticatedUser(response.token, response.account);
        
        // Navigate using React Router (maintains SPA behavior)
        navigate(`/${currentLanguage}${emailConfirmedRoute}?success=true&authenticated=true`);
      } else {
        navigate(`/${currentLanguage}${emailConfirmedRoute}?success=true`);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        console.error('Email confirmation error:', error.message);
      } else {
        console.error('Email confirmation error:', error);
      }
      const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
      const emailConfirmedRoute = t('routes.emailConfirmed');
      navigate(`/${currentLanguage}${emailConfirmedRoute}?success=false`);
    } finally {
      setIsProcessing(false);
    }
  }, [token, hasConfirmed, navigate, setAuthenticatedUser, t, i18n.language]);

  useEffect(() => {
    // Only run if we haven't confirmed yet and have a token (check both state and ref)
    if (!hasConfirmed && !confirmationAttempted.current && token) {
      confirmEmail();
    } else if (!token && !confirmationAttempted.current) {
      // Handle case where no token is provided
      const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
      const emailConfirmedRoute = t('routes.emailConfirmed');
      navigate(`/${currentLanguage}${emailConfirmedRoute}?success=false`);
      setIsProcessing(false);
    }
  }, [confirmEmail, token, hasConfirmed, navigate, t, i18n.language]);

  if (isProcessing) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '50vh',
          textAlign: 'center'
        }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            {t('emailConfirmation.processing')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('emailConfirmation.processingDetails')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return null;
};

export default EmailConfirmation;